import { Table, TableBody, TableContainer } from '@mui/material';
import { Box } from '@mui/material';
import { TransactionDetailed } from '@/models/transaction';
import { useFormatting } from '@/utils/formatting';
import { Link } from 'react-router-dom';
import { useContext } from 'react';
import { UserContext } from '@/contexts/user/UserContext';
import {
    TableCell,
    TableRow,
    TableHeader,
    TableHeaderCell
} from '@/components/shared/styles/Table';
import { Checkbox } from '@/components/shared/CheckBox';
import Chip from '@/components/shared/Chip';
import { useTransactionModalStore } from '@/pages/history/HistoryStore';

export type TransactionsListProps = {
    transactions: Array<TransactionDetailed>;
    checked: { transactionId: string; transactionStatus: string }[];
    handleToggle?: (transactionId: string, status: any) => void;
    handleAllToggle?: () => () => void;
};

export const TransactionsApprovalTable = (props: TransactionsListProps) => {
    const { getBalanceLabel, formatDateTime } = useFormatting();
    const { company } = useContext(UserContext);

    const isChecked = (transactionId: string) => {
        return props.checked.some(
            (item) => item.transactionId === transactionId
        );
    };

    const openModel = useTransactionModalStore((state) => state.openModal);

    const handleOpenTransactionDetail = (transaction: TransactionDetailed) => {
        openModel({
            transactionId: transaction.transactionId,
            status: transaction.status,
            createdAt: transaction.createdAt,
            amount: transaction.amount,
            balance: 0,
            companyName: transaction.companyName,
            currency: transaction.currency,
            userId: transaction.user.userId,
            userIsDeleted: false,
            transactionType: '',
            userName: transaction.user.userName,
            executantAdminEmail: transaction.user.email,
            approvalType: 'Manual',
            withdrawalFee: transaction?.withdrawalFee,
            totalAmount: transaction?.totalAmount
        });
    };

    return (
        <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell sx={{ fontWeight: 'bold' }}>
                            <Checkbox
                                onClick={
                                    props.handleAllToggle &&
                                    props.handleAllToggle()
                                }
                                sx={{ marginLeft: '10px', padding: 0 }}
                                edge="start"
                                checked={
                                    props.checked.length ===
                                    props.transactions.length
                                }
                                tabIndex={-1}
                                inputProps={{
                                    'aria-labelledby':
                                        'checkbox-list-secondary-label-all'
                                }}
                            />
                        </TableHeaderCell>
                        <TableHeaderCell sx={{ fontWeight: 'bold' }}>
                            Name
                        </TableHeaderCell>
                        <TableHeaderCell
                            align="right"
                            sx={{ fontWeight: 'bold' }}
                        >
                            Available
                        </TableHeaderCell>
                        <TableHeaderCell
                            align="right"
                            sx={{ fontWeight: 'bold' }}
                        >
                            Withdrawn
                        </TableHeaderCell>
                        <TableHeaderCell sx={{ fontWeight: 'bold' }}>
                            Created
                        </TableHeaderCell>
                        <TableHeaderCell sx={{ fontWeight: 'bold' }}>
                            Status
                        </TableHeaderCell>
                        <TableHeaderCell
                            align="right"
                            sx={{ fontWeight: 'bold' }}
                        >
                            Requested
                        </TableHeaderCell>
                        <TableHeaderCell
                            align="right"
                            sx={{ fontWeight: 'bold' }}
                        ></TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody sx={{ cursor: 'pointer' }}>
                    {props.transactions.map((transaction) => {
                        const labelId = `checkbox-list-secondary-label-${transaction.transactionId}`;
                        return (
                            <TableRow key={transaction.transactionId}>
                                <TableCell width="1%">
                                    <Checkbox
                                        sx={{ marginLeft: '10px', padding: 0 }}
                                        edge="start"
                                        checked={isChecked(
                                            transaction.transactionId
                                        )}
                                        onClick={() => {
                                            props?.handleToggle?.(
                                                transaction.transactionId,
                                                transaction?.status
                                            );
                                        }}
                                        tabIndex={-1}
                                        inputProps={{
                                            'aria-labelledby': labelId
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Box
                                        sx={{
                                            fontWeight: 'bold',
                                            display: 'inline'
                                        }}
                                    >
                                        <Link
                                            style={{
                                                textDecoration: 'none',
                                                color: 'black',
                                                textDecorationLine: 'underline',
                                                marginRight: '5px'
                                            }}
                                            to={{
                                                pathname: `/users/${transaction.user.userId}`
                                            }}
                                        >
                                            {transaction.user.userName}
                                        </Link>
                                    </Box>
                                    {transaction.user.email}
                                </TableCell>
                                <TableCell
                                    onClick={() => {
                                        handleOpenTransactionDetail(
                                            transaction
                                        );
                                    }}
                                    align="right"
                                >
                                    <Box>
                                        {getBalanceLabel(
                                            transaction.user.balance,
                                            company.currency
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell
                                    onClick={() => {
                                        handleOpenTransactionDetail(
                                            transaction
                                        );
                                    }}
                                    align="right"
                                >
                                    {getBalanceLabel(
                                        transaction.user.withdrawed,
                                        company.currency
                                    )}
                                </TableCell>
                                <TableCell
                                    onClick={() => {
                                        handleOpenTransactionDetail(
                                            transaction
                                        );
                                    }}
                                    align="left"
                                >
                                    {formatDateTime(transaction?.createdAt)}
                                </TableCell>
                                <TableCell
                                    onClick={() => {
                                        handleOpenTransactionDetail(
                                            transaction
                                        );
                                    }}
                                >
                                    <Chip
                                        color="warning"
                                        icon={<span></span>}
                                        label={transaction?.status}
                                    />
                                </TableCell>
                                <TableCell
                                    onClick={() => {
                                        handleOpenTransactionDetail(
                                            transaction
                                        );
                                    }}
                                    align="right"
                                >
                                    {getBalanceLabel(
                                        transaction.amount,
                                        transaction.currency
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
